/* @flow */
import { graphql, compose } from 'react-apollo';
import { connect } from 'react-redux';
import gql from 'graphql-tag';
import * as React from 'react';

import LinkDeviceModal from 'user/components/LinkDeviceModal';
import Button from 'components/ButtonDeprecated';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import GroupsContentHeader from 'groups/components/GroupsContentHeader';
import MakeACallModal from 'phoneCalls/components/MakeACallModal';
import ModalTrigger from 'components/ModalTrigger';
import PhoneCallAddNumberPage from 'phoneCalls/components/PhoneCallAddNumberPage';
import PhoneCallsLogTable from 'phoneCalls/components/PhoneCallsLogTable';
import PhoneCallsNull from 'phoneCalls/components/PhoneCallsNull';
import ErrorReporter from 'app/components/ErrorReporter';
import View from 'components/View';

import type { PhoneCall } from 'phoneCalls/components/PhoneCallsLogTableRow';
import type { State as PushedPhoneCalls } from 'phoneCalls/reducers/phoneCalls';

import './styles/PhoneCallsTab.scss';

type Props = {
  phoneCalls: ?(PhoneCall[]),
  hasSMSDevice: ?boolean,
  loading: boolean,
  pushedPhoneCalls: PushedPhoneCalls,
  refetch: Function,
};

class PhoneCallsTab extends React.Component<Props> {
  componentWillReceiveProps(nextProps: Props) {
    // This compares the number of stored phone calls.
    // We could just compare the object since they are immutablejs map
    // to refetch on every push update. But the correct way to acheive this
    // would be to write the apollo cache when we receive the push events.
    // Unfortunatly we cannot do this at the moment because the data in the push
    // is missing important displayed data (the status_localized_key).
    //
    // This refetch is only useful for when user starts a call from the
    // "Make a call" button bellow (because we reload the phone calls
    // with a cache-and-network policy).
    if (this.props.pushedPhoneCalls.size !== nextProps.pushedPhoneCalls.size) {
      this.props.refetch();
    }
  }

  render() {
    const { loading, phoneCalls, refetch, hasSMSDevice } = this.props;

    const content = (() => {
      if (!loading && !Array.isArray(phoneCalls)) {
        return (
          <ErrorReporter error="phone_call_tab.cannot_load">
            <span>Oops, could not load phone calls</span>
          </ErrorReporter>
        );
      } else if (!Array.isArray(phoneCalls)) {
        return <CenteredLoadingIndicator />;
      } else if (!hasSMSDevice) {
        return <PhoneCallAddNumberPage onDone={refetch} />;
      } else if (phoneCalls && phoneCalls.length > 0) {
        return <PhoneCallsLogTable phoneCalls={phoneCalls} />;
      } else {
        return <PhoneCallsNull />;
      }
    })();

    const button = (() => {
      if (hasSMSDevice === null) {
        return null;
      } else if (hasSMSDevice) {
        return (
          <ModalTrigger modal={<MakeACallModal />}>
            <Button eventElementName="make_a_call_button_header">Make a call</Button>
          </ModalTrigger>
        );
      } else {
        return (
          <ModalTrigger
            modal={
              <LinkDeviceModal
                skipUpdateDevicePreferences
                onDone={refetch}
                deviceType="phoneNumber"
              />
            }>
            <Button eventElementName="add_phone_number_button_header">Add your phone number</Button>
          </ModalTrigger>
        );
      }
    })();

    return (
      <div className="PhoneCallsTab">
        <GroupsContentHeader
          actions={button}
          title="Recent calls"
          className="PhoneCallsTab-header"
        />
        <View gutter="small" scroll className="PhoneCallsTab-body">
          {content}
        </View>
      </div>
    );
  }
}

const withData = graphql(
  gql`
    query PhoneCallsTab {
      user: me {
        id
        uuid
        phone_calls(limit: 50) {
          items {
            uuid
            otherUser {
              uuid
              id
              name
              initials
              color
              contactability_status
              chat_eligibility_state
              canPhoneDenialReason
              canPhone
              profilePictureUrl
            }
            duration
            started_at
            status_localization_key
            status
          }
        }
      }
      devices {
        id
        uuid
        type
      }
    }
  `,
  {
    options: { fetchPolicy: 'cache-and-network' },
    props: ({ data }) => ({
      phoneCalls: data.user ? data.user.phone_calls.items : null,
      hasSMSDevice: data.devices ? !!data.devices.find(d => d.type === 'sms') : null,
      loading: data.loading,
      refetch: data.refetch,
    }),
  }
);

function mapStateToProps(state: *) {
  return {
    pushedPhoneCalls: state.phoneCalls,
  };
}

export { PhoneCallsTab };

// $FlowFixMe
export default compose(withData, connect(mapStateToProps, undefined))(PhoneCallsTab);
